import {
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Column } from "primereact/column";
import { TableHeader } from "../../components/datatable/TableHeader";
import { FormSkeleton } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { getGlobalConfigurationPageLabel } from "../../utils";
import { AppContext } from "../../context/index";
import { Button, Dropdown } from "iuvity-ui-components";
import { Tag } from "iuvity-ui-components/dist/tag";
import { MultiSelect } from 'primereact/multiselect';
import { registerAudit } from "../../helpers/registerAudit";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";
import {
  importDataStructureConverter,
  downloadInfo,
} from "../../utils/index";
import { formatStringToDate2 } from "../../helpers/formatDate";

export const ErrorEnvImportPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { language, permissiondInfoByRole, errorConfigsList, configsImportProcessed, tenantParentName, selectedEnvironment },
  } = useContext(AppContext);

  const [errorDataToShow, setErrorDataToShow] = useState<any>([]);
  const [filteredData, setFilteredData] = useState<any[] | null>(errorDataToShow);

  useEffect(()=>{
    if ( !!errorConfigsList && Object.keys(errorConfigsList)?.length > 0 ) {
      const {errorDataToShow: errorsParsed} = importDataStructureConverter(t, errorConfigsList, true);
      setErrorDataToShow(errorsParsed);
      setFilteredData(errorsParsed);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[errorConfigsList]);

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const customPageLabel = getGlobalConfigurationPageLabel(t, location.pathname);
  const [statusDrodownValue, setStatusDrodownValue] = useState<string>('');
  const [valuesOfColumnFilterFields, setValuesOfColumnFilterFields] = useState<{
    functionality: string[];
    type: string[];
    version: string[];
    validations: string[];
    name: string[];
  }>({
    functionality: [],
    type: [],
    version: [],
    validations: [],
    name: [],
  });
  const [disableClearFilterButton, setDisableClearFilterButton] = useState<{
    status: boolean;
    type: boolean;
    functionality: boolean;
    version: boolean;
    validations: boolean;
    name: boolean;
  }>({
    status: false,
    type: false,
    functionality: false,
    version: false,
    validations: false,
    name: false,
  });
  const [disableApplyFilterButton, setDisableApplyFilterButton] = useState<{
    status: boolean;
    type: boolean;
    functionality: boolean;
    version: boolean;
    validations: boolean;
    name: boolean;
  }>({
    status: false,
    type: false,
    functionality: false,
    version: false,
    validations: false,
    name: false,
  });

  const columns = useMemo(() => {
    return [
      {
        field: "status",
        header: t("Environments.statusLabel"),
      },
      {
        field: "validations",
        header: t("Translations.validationsLabel"),
      },
      {
        field: "id",
        header: "ID",
      },
      {
        field: "functionality",
        header: t("Translations.functionalityLabel"),
      },
      {
        field: "type",
        header: t("Environments.typeLabel"),
      },
      {
        field: "name",
        header: t("Environments.nameLabel"),
      },
      {
        field: "version",
        header: t("Environments.versionLabel"),
      },
      {
        field: "createdat",
        header: t("Translations.createdatLabel"),
      },
      {
        field: "updatedat",
        header: t("Translations.updatedatLabel"),
      },
    ];
  }, [t]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    name: { value: null, matchMode: FilterMatchMode.IN },
    type: { value: null, matchMode: FilterMatchMode.IN },
    functionality: { value: null, matchMode: FilterMatchMode.IN },
    validations: { value: null, matchMode: FilterMatchMode.IN },
    version: { value: null, matchMode: FilterMatchMode.IN },
  };

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);

  const onClearFilters = () => {
    setFilteredData(errorDataToShow);
  };

  const onClearFiltersColumn = () => {
    setFilters(defaultFilterValues);
    setStatusDrodownValue('');
    setValuesOfColumnFilterFields({
      functionality: [],
      type: [],
      version: [],
      validations: [],
      name: [],
    });
  };

  const onClearFiltersPerTable = ( options: any, filtersFromSession: any ) => {
    setFilters({
      global: { value: options?.field === 'global' ? null : filtersFromSession?.global?.value, matchMode: FilterMatchMode.CONTAINS },
      status: { value: options?.field === 'status' ? null : filtersFromSession?.status?.value, matchMode: FilterMatchMode.EQUALS },
      name: { value: options?.field === 'name' ? null : filtersFromSession?.name?.value, matchMode: FilterMatchMode.IN },
      type: { value: options?.field === 'type' ? null : filtersFromSession?.type?.value, matchMode: FilterMatchMode.IN },
      functionality: { value: options?.field === 'functionality' ? null : filtersFromSession?.functionality?.value, matchMode: FilterMatchMode.IN },
      validations: { value: options?.field === 'validations' ? null : filtersFromSession?.validations?.value, matchMode: FilterMatchMode.IN },
      version: { value: options?.field === 'version' ? null : filtersFromSession?.version?.value, matchMode: FilterMatchMode.IN },
    });

    // luego de dar click en limpiar, elimina los criterios de búsqueda.
    setDisableClearFilterButton((prev:any)=>({
      ...prev,
      [options?.field]: true,
    }));
    options?.field === 'status'
      ? setStatusDrodownValue('')
      : setValuesOfColumnFilterFields((prev)=>({
          ...prev,
          [options.field]: ['widgets','type','version'].includes(options.field) ? [] : '',
        }));
    setDisableApplyFilterButton((prev)=>({
      ...prev,
      [options?.field]: false
    }));
  };

  const onDownloadErrors = () => {
    downloadInfo(
      `${t("Environments.errorsFilenameTitle")}_${
        selectedEnvironment?.fullname
      }_${t("Environments.filenameExtraInfo")}${
        new Date().toISOString().substring(0, 10).replace(/-|:/g, "") +
        "-" +
        new Date().toLocaleTimeString("es-ES").replace(/-|:/g, "")
      }`,
    //   Object.fromEntries(
    //     Object.entries(errorData).filter(
    //       ([_, value]) => (value as any).length > 0
    //     )
    //   )
    errorDataToShow ?? []
  );
  };

  const handleModalProps = async (
    type: "EXPORT",
  ) => {
    switch (type) {
      case "EXPORT":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Environments.errorExportModalTitle"),
          content: t("Environments.errorExportModalContentt"),
          onConfirm: () => {
            onDownloadErrors();
            registerAudit({
              role: "",
              group: "",
              tenant: tenantParentName,
              environment: "",
              segment: "",
              functionality: "ADMIN_APLICATIONS",
              activity: "NOPROCCESS_DOWNLOAD_CONFIGURATIONS",
              object: "APPLICATION_ENVIRONMENT",
              initialValue: null,
              finalValue: `${errorDataToShow?.length}`,
              status: "SUCCESS",
              errorCode: "",
              errorDescription: "",
              errorCause: "",
            });
          },
        }));

      default:
        return;
    }
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: col.field === 'id'
                ? 'ID'
                : (['validations', 'createdat', 'updatedat', 'functionality'].includes(col.field)
                  ? t(`Translations.${col.field}Label`)
                  : t(`Environments.${col.field}Label`)),
      }))
      );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };

  const multiselectFilterTemplate = (options: any, arrayWithoutDuplicates: any) => {
    return (<>
      <label htmlFor={options.field} className="block lb mt-2 mb-1">
        {t(`${options.field === 'name' ? 'Environments' : 'Translations'}.${options.field}Label`)}
      </label>
      <MultiSelect
        // @ts-ignore
        value={valuesOfColumnFilterFields[options.field]}
        display="chip"
        options={arrayWithoutDuplicates}
        itemTemplate={ItemsTemplate}
        placeholder={t(`${options.field === 'name' ? 'Environments' : 'Translations'}.${options.field === 'validations' ? 'error' : options.field}PlaceholderLabel`)} 
        className="mb-2"
        emptyFilterMessage={t("Translations.noAvailableMessage")}
        filter
        onChange={(e)=>{
          setValuesOfColumnFilterFields((prev)=>({
            ...prev,
            [options.field]: e.value,
          }));
          options.filterCallback(e.value);
          setDisableApplyFilterButton((prev)=>({
            ...prev,
            [options.field]: e?.value?.length > 0
          }));
        }}
      />
    </>)
  };

  const filterTemplate = (options: any) => {
    const arrayOriginal = !['functionality', 'type'].includes(options?.field)
      ? errorDataToShow?.map((config: any) =>
          ({
            label: config[`${options.field}`],
            value: config[`${options.field}`]
          }))
          ?.filter((item: { label: string, value: string }) => item.label)
      : [...new Set(errorDataToShow.flatMap((item: any) => t(`Environments.${item[options?.field]?.toLowerCase()}Label`)))]
        .map((item) => ({
          value: `${item}`.trim(),
          label: `${item}`.trim(),
        }));

    arrayOriginal?.sort((a: any, b: any) => a.label.localeCompare(b.label));
    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      options.field === 'status'
      ? statusFilterTemplate(options)
      : multiselectFilterTemplate(options, arrayWithoutDuplicates)
    )
  };

  const statuses:any = [
    `${t("Translations.INVALIDStatusLabel")}`,
  ];

  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  };

  const statusFilterTemplate = (options: any) => {
    return (
            <div>
              <label htmlFor={options.field} className="block lb mt-2 mb-3">
                {t(`Translations.${options.field}Label`)}
              </label>
              <Dropdown
                label=""
                value={statusDrodownValue}
                options={statuses}
                itemTemplate={statusItemTemplate}
                placeholder={t(`Translations.${options.field}FilterPlaceholder`)}
                className="p-column-filter mb-2"
                showClear={false}
                onChange={(e)=>{
                  setStatusDrodownValue(e.value);
                  options.filterCallback(t("Translations.INVALIDStatusLabel") === e.value ? 'INVALID' : 'VALID');
                  setDisableApplyFilterButton((prev)=>({
                    ...prev,
                    status: true
                  }));
                }}
              />
            </div>
    )
  };

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        itemID={options?.field}
        label={t("Tenants.applyFilterLabel")}
        className="p-button"
        // @ts-ignore
        disabled={!disableApplyFilterButton[options.field]}
        onClick={() => {
          options.filterApplyCallback();
          setDisableClearFilterButton((prev:any)=>({
            ...prev,
            [options?.field]: false,
          }));
        }}
      />
    )
  };

  const filterClearTemplate = (options: any) => {
    const filtersFromSession = JSON.parse(sessionStorage?.getItem('import-config-filters-table') ?? '{}')?.filters;

    return (
      <Button
        label={t("Tenants.clearFilterLabel")}
        variant="p-button-text"
        disabled={(filtersFromSession?.hasOwnProperty('global')
            ? !filtersFromSession[options?.field]?.value : true)
            // @ts-ignore
            || disableClearFilterButton[options?.field]}
        onClick={() => onClearFiltersPerTable(options, filtersFromSession)}
      />
    )
  }

  const statusBodyTemplate = (rowData: any) => (
    <Tag
      value={t(`Translations.${rowData.status}StatusLabel`)}
      severity={
        (['VALID'].includes(rowData.status) && 'success') ||
        (['INVALID'].includes(rowData.status) && 'danger') ||
        'warning'
      }
    />
  );

  useEffect(() => {
    if (sessionStorage.getItem('import-config-filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('import-config-filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  return (
    <>
      <TransactionalSubheader
        subheaderTitle={
          <div
            style={{
              marginLeft: '-15px',
            }}
          >
            {t("Environments.errorConfigsPageTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={customPageLabel}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => {
              navigate("/");
              onClearFilters();
            },
          }}
          breadcrumbsItems={[
            {
              label: t("Applications.applicationsBreadcrumbLabel"),
              style: { fontWeight: "bold" },
              disabled: true
            },
            {
              label: t("Applications.applicationAdminTitle"),
              command: () => navigate("/applications"),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: t("Applications.editApplicationTitle"),
              command: () => navigate("/applications/edit"),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
            },
            {
              label: t("Applications.importApplicationTitle"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content ml-0 mr-0">
          <span
            dangerouslySetInnerHTML={
              { __html: t("Environments.errorConfigsPageDescription")
                .replace('{{totalconfigsProcessed}}', (configsImportProcessed ?? 0) - (errorDataToShow?.length ?? 0))
                .replace('{{totalconfigsToProcess}}', configsImportProcessed ?? 0) }}
          />
        </div>
      </TransactionalSubheader>

      <SubheaderActions
        className="multiactions-c"
        actions={subheaderActionsPrivilegeValidator([
          {
            id: "111",
            icon: "iu iu-icon-upload",
            label: t("Translations.exportLabelsWithErrorsButton"),
            style: {
              display: (!permissiondInfoByRole.READ && !permissiondInfoByRole.CREATE) ? 'none' : undefined,
            },
            onClick: () => handleModalProps("EXPORT"),
          },
        ])}
      />

      <BoxFull className="dt-c my-4">
        {filteredData ? (
          <DataTable
            header={
              <TableHeader
                title={t("Environments.configurationsTableTitle")}
                columns={columns}
                selectedColumns={selectedColumns}
                toggleColumns={setSelectedColumns}
                filters={filters}
                setFilters={setFilters}
                reload={()=>{}}
                onClearFiltersColumn={onClearFiltersColumn}
                globalFilterValue={globalFilterValue}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={t("TableActions.emptyMessage")}
            rows={10}
            size="normal"
            editMode="row"
            dataKey="id"
            sortField="functionality"
            sortOrder={1}
            filters={filters}
            value={filteredData.map((configuration: any) => ({
                ...configuration,
                functionality: t(`Environments.${configuration?.functionality.toLowerCase()}Label`),
                type: t(`Environments.${configuration?.type}Label`),
                status: 'INVALID',
                createdat: !!configuration?.createdat ? formatStringToDate2(configuration?.createdat, true, t) : null,
                updatedat: !!configuration?.updatedat ? formatStringToDate2(configuration?.updatedat, true, t) : null,
              }))
            }
            scrollable
            resizableColumns
            filterDisplay="menu"
            stateStorage="session"
            stateKey="import-config-filters-table"
          >
            {selectedColumns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable
                align="left"
                style={{
                  whiteSpace: "normal",
                  minWidth: "14rem",
                }}
                filterField={col.field}
                showFilterMatchModes={false}
                filterMenuStyle={{ width: '23rem'}}
                filter={["id","createdat","updatedat"].includes(col.field) ? false : true}
                filterElement={filterTemplate}
                filterApply={filterApplyTemplate}
                filterClear={filterClearTemplate}
                body={(rowData) => col.field === 'status' ? statusBodyTemplate(rowData) : rowData[col.field]}
              />
            ))}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
        )}
      </BoxFull>

      <div
        className="w-full"
        style={{
          paddingRight: '128px',
        }}
      >
        <div
          className="flex w-full my-5 p-0 gap-6"
          style={{
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="p-button-link"
            label={t(`Environments.omitBtnLabel`)}
            onClick={() => navigate('/applications/edit')}
          />
          <Button
              variant="p-button"
              label={t("Translations.exportBtnLabel")}
              onClick={() => handleModalProps("EXPORT")}
            />
        </div>
      </div>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
